import { logAnalyticsEvent } from '@/services/analyticsLogger';
import { logger } from '@/services/logger';

type SanitizationScope = 'travel_news' | 'weather_forecast' | 'places_cache';
type SanitizationReason =
  | 'missing_country'
  | 'empty_city'
  | 'invalid_country_code'
  | 'undefined_fields_removed';

type SanitizationIntercept = {
  scope: SanitizationScope;
  reason: SanitizationReason;
  droppedFields?: string[];
  removedCount?: number;
};

const counters = new Map<string, number>();
const lastEmitAtByKey = new Map<string, number>();
const EMIT_EVERY_N = 25;
const EMIT_MIN_INTERVAL_MS = 2 * 60 * 1000;

const dedupeFields = (fields: string[] | undefined): string[] | undefined => {
  if (!fields?.length) return undefined;
  return Array.from(new Set(fields.filter((field) => field.trim().length > 0))).slice(0, 10);
};

export const trackSanitizationIntercept = ({
  scope,
  reason,
  droppedFields,
  removedCount,
}: SanitizationIntercept): void => {
  const key = `${scope}:${reason}`;
  const nextCount = (counters.get(key) ?? 0) + 1;
  counters.set(key, nextCount);

  const now = Date.now();
  const lastEmitAt = lastEmitAtByKey.get(key) ?? 0;
  const shouldEmit =
    nextCount === 1 ||
    nextCount % EMIT_EVERY_N === 0 ||
    now - lastEmitAt >= EMIT_MIN_INTERVAL_MS;

  if (!shouldEmit) {
    return;
  }

  lastEmitAtByKey.set(key, now);

  const normalizedDroppedFields = dedupeFields(droppedFields);
  logger.info('[Sanitization] Intercepted invalid payload', {
    scope,
    reason,
    count: nextCount,
    droppedFields: normalizedDroppedFields,
    removedCount,
  });

  void logAnalyticsEvent('sanitization_intercepted', {
    scope,
    reason,
    count: nextCount,
    ...(normalizedDroppedFields ? { droppedFields: normalizedDroppedFields } : {}),
    ...(typeof removedCount === 'number' ? { removedCount } : {}),
  });
};
